import {
  HHBaseTable,
  HHButton,
  HHCircularProgress,
  HHIconButton,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import { Close } from '@mui/icons-material';
import CheckCircle from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import dayjs from 'dayjs';
import { lowerCase } from 'lodash';
import { useEffect, useState } from 'react';
import { useGetTemporalWorkflowLazyQuery } from '../../../../types';
import { capitalizeFirstLetterAndSpaceCamelCaseString } from '../../../../utils/bills-utils';
import {
  persistStatus,
  removeStatus,
  StatusStorageKeys,
} from '../../../../utils/local-storage-utils';

export enum EligibilityCheckStatus {
  RUNNING = 'RUNNING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  CANCELED = 'CANCELED',
  TERMINATED = 'TERMINATED',
  TIMED_OUT = 'TIMED_OUT',
}

export type EligibilityCheckResult = {
  workflowType?: string;
  status?: string;
  result?: string;
  failureMessage?: string | null;
  initiatedAt?: string;
};

export type EligibilityCheckResultRecord = Record<
  string,
  EligibilityCheckResult
>;

export const EligibilityStatusModal = (): JSX.Element => {
  const [localResults, setLocalResults] =
    useState<EligibilityCheckResultRecord>({});

  const [getWorkflowStatus, { loading }] = useGetTemporalWorkflowLazyQuery();

  useEffect(() => {
    const storedData =
      localStorage.getItem(StatusStorageKeys.eligibilityCheckStatus) || '{}';
    setLocalResults(JSON.parse(storedData));
  }, [setLocalResults]);

  const checkWorkflowStatus = (workflowId: string): void => {
    getWorkflowStatus({
      variables: {
        workflowId,
      },
      fetchPolicy: 'no-cache',
    }).then(({ data }) => {
      if (!data?.getTemporalWorkflow) {
        throw new Error('Failed to fetch workflow status');
      }
      const currentStatuses = persistStatus(
        { ...data.getTemporalWorkflow },
        StatusStorageKeys.eligibilityCheckStatus,
        workflowId,
      );
      setLocalResults(currentStatuses as EligibilityCheckResultRecord);
    });
  };

  const removeWorkflowStatus = (workflowId: string): void => {
    setTimeout(() => {
      const currentStatuses = removeStatus(
        StatusStorageKeys.eligibilityCheckStatus,
        workflowId,
      );
      setLocalResults(currentStatuses as EligibilityCheckResultRecord);
    }, 500);
  };

  const getStatusIcon = (status: string): JSX.Element => {
    switch (status) {
      case EligibilityCheckStatus.COMPLETED:
        return <CheckCircle color="success" />;
      case EligibilityCheckStatus.FAILED:
      case EligibilityCheckStatus.CANCELED:
      case EligibilityCheckStatus.TERMINATED:
        return <ErrorIcon color="error" />;
      case EligibilityCheckStatus.TIMED_OUT:
        return <ErrorIcon color="warning" />;
      case EligibilityCheckStatus.RUNNING:
      default:
        return <HHCircularProgress size={20} />;
    }
  };

  const renderResultEntries = (
    obj: Record<string, unknown>,
  ): { key: string; label: string; displayValue: string }[] =>
    Object.entries(obj).flatMap(([key, value]) => {
      if (value === null || value === undefined) return [];
      if (typeof value === 'object' && !Array.isArray(value)) {
        return renderResultEntries(value as Record<string, unknown>);
      }
      return [
        {
          key,
          label: capitalizeFirstLetterAndSpaceCamelCaseString(key),
          displayValue: String(value),
        },
      ];
    });

  const tableHeaders = [
    { value: 'Workflow Type', align: 'left' as const },
    { value: 'Status', align: 'left' as const },
    { value: 'Result Summary', align: 'left' as const },
    { value: 'Initiated At', align: 'left' as const },
    { value: '', align: 'right' as const },
  ];

  const bodyContent = Object.entries(localResults).map(
    ([workflowId, result]) => ({
      workflowType: {
        value: lowerCase(result.workflowType ?? ''),
      },
      status: {
        value: getStatusIcon(result.status ?? ''),
      },
      result: {
        value: (
          <HHStack direction="column">
            {result.failureMessage && (
              <div>
                <strong>Error: {result.failureMessage}</strong>
              </div>
            )}
            {result.result && typeof result.result === 'object'
              ? renderResultEntries(
                  result.result as Record<string, unknown>,
                ).map(({ key, label, displayValue }) => (
                  <div key={key}>
                    <strong>
                      {label}: {displayValue}
                    </strong>
                  </div>
                ))
              : result.result}
          </HHStack>
        ),
      },
      initiatedAt: {
        value: result.initiatedAt
          ? dayjs(result.initiatedAt).format('L LTS')
          : '--',
      },
      checkWorkflowStatus: {
        value: (
          <HHStack direction="row" spacing={2} justifyContent="flex-end">
            <HHButton
              hhVariant="variant-bypass"
              variant="contained"
              size="small"
              disabled={loading}
              onClick={(): void => {
                checkWorkflowStatus(workflowId);
              }}
            >
              Check Status
            </HHButton>
            <HHIconButton
              hhVariant="variant-bypass"
              color="secondary"
              size="small"
              data-testid="remove-workflow-status-button"
              disabled={loading}
              onClick={(): void => {
                removeWorkflowStatus(workflowId);
              }}
            >
              <Close />
            </HHIconButton>
          </HHStack>
        ),
      },
    }),
  );

  return (
    <HHStack direction="column" spacing={3}>
      {Object.keys(localResults).length > 0 ? (
        <HHStack direction="column" spacing={2}>
          <HHStack
            sx={{
              maxHeight: '300px',
              border: '1px solid rgba(0, 0, 0, 0.12)',
              borderRadius: 1,
              overflow: 'auto',
            }}
          >
            <HHBaseTable
              tableHeaders={tableHeaders}
              bodyContent={bodyContent}
              sortByColumn={{ columnIndex: 3, sortDirection: 'desc' }}
              headerRowBottomBorder="1px solid rgba(0, 0, 0, 0.12)"
              bodyRowBorder={{
                borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
              }}
              bodyTypographyVariant="body"
            />
          </HHStack>
        </HHStack>
      ) : (
        <HHTypography hhVariant="muted">No results available</HHTypography>
      )}
    </HHStack>
  );
};
